import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, LineChart, Wallet, Star, History, Sparkles, LogOut, TrendingUp, Sun, Moon, Settings as SettingsIcon, Link2, Bot, Menu, X, Sigma, CalendarDays, Rocket, Bell, BellRing, Landmark, Activity, PlayCircle, Trophy, ShieldCheck, Workflow, Zap, Copy, ChevronDown, Users, BarChart3, ArrowLeftRight, Search, DollarSign, Gift, Target, LifeBuoy, ShieldAlert } from "lucide-react";
import KillSwitchButton from "./KillSwitchButton";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import NotificationBell from "./NotificationBell";

import { useAuth } from "../context/AuthContext";
import { useAlerts, useLiveStream } from "../context/LiveStreamContext";
import { fmtMoney, api } from "../lib/api";
import { native } from "../lib/native";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

// Grouped navigation. Top-level items with `to` are single links; items with
// `children` render as collapsible dropdowns. Every route in App.js is
// reachable from exactly one place here — no duplicates.
const navGroups = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  {
    label: "Portfolio", icon: Wallet, children: [
      { to: "/portfolio", label: "Portfolio", icon: Wallet },
      { to: "/history", label: "Journal", icon: CalendarDays },
    ],
  },
  {
    label: "Trade", icon: ArrowLeftRight, children: [
      { to: "/orders", label: "Orders", icon: History },
      { to: "/bots", label: "Auto Trade", icon: Bot },
      { to: "/advanced-orders", label: "Advanced Orders", icon: Zap },
      { to: "/alerts", label: "Alerts", icon: Bell },
      { to: "/strategies", label: "Strategies", icon: Workflow },
      { to: "/ibkr", label: "IBKR", icon: Landmark },
      { to: "/go-live", label: "Go Live", icon: DollarSign },
    ],
  },
  {
    label: "Research", icon: Search, children: [
      { to: "/markets", label: "Markets", icon: LineChart },
      { to: "/setups", label: "Setups Today", icon: Target },
      { to: "/backtest", label: "Backtest", icon: PlayCircle },
      { to: "/options", label: "Options", icon: Sigma },
      { to: "/ipos", label: "IPOs", icon: Rocket },
      { to: "/ai", label: "AI Coach", icon: Sparkles },
    ],
  },
  {
    label: "Community", icon: Users, children: [
      { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
      { to: "/copy", label: "Copy Traders", icon: Copy },
      { to: "/earnings", label: "Earnings", icon: DollarSign },
      { to: "/referrals", label: "Refer & Earn", icon: Gift },
    ],
  },
  { to: "/risk", label: "Risk", icon: ShieldCheck },
  { to: "/security", label: "Security", icon: ShieldCheck },
  { to: "/risk-settings", label: "Risk limits", icon: ShieldAlert },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
  { to: "/help", label: "Help", icon: LifeBuoy },
];

// Bottom-nav shows the 5 most-used sections on mobile
const bottomNav = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/portfolio", label: "Folio", icon: Wallet },
  { to: "/markets", label: "Markets", icon: LineChart },
  { to: "/bots", label: "Bots", icon: Bot },
  { to: "/ai", label: "Coach", icon: Sparkles },
];

// Collapsible nav-group with an active-child-aware open state.
function NavGroup({ group, currentPath, onNavigate }) {
  const containsActive = useMemo(
    () => group.children.some((c) => currentPath.startsWith(c.to)),
    [group.children, currentPath],
  );
  const [open, setOpen] = useState(containsActive);
  useEffect(() => { if (containsActive) setOpen(true); }, [containsActive]);
  const Icon = group.icon;
  const testKey = group.label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-group-${testKey}`}
        aria-expanded={open}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          containsActive ? "text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
        }`}>
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-border/60 space-y-0.5" data-testid={`nav-group-${testKey}-menu`}>
          {group.children.map((c) => {
            const CIcon = c.icon;
            return (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={onNavigate}
                data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive ? "bg-secondary text-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`
                }>
                <CIcon className="w-3.5 h-3.5" />
                {c.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved ? saved === "dark" : true;
  });
  const [ibkr, setIbkr] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [ipoHot, setIpoHot] = useState([]);
  const seenAlertIds = useRef(new Set()); // survives across polls, avoids duplicate toasts
  const firedDrawAlerts = useRef(new Set()); // key: symbol|price|direction — fire once per cross

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    api.get("/ibkr/status").then((r) => setIbkr(r.data)).catch(() => {});
  }, [user?.account_mode]);

  // Hot IPO alerts poll — refresh every 2 minutes. Uses a ref (seenAlertIds) so
  // fresh toasts only fire for alerts we haven't seen yet in this session.
  useEffect(() => {
    if (!user) return;
    let firstRun = true;
    const load = () => api.get("/ipos/alerts").then((r) => {
      const hot = r.data.hot || [];
      const fresh = hot.filter((h) => !seenAlertIds.current.has(`${h.ticker}|${h.company}`));
      hot.forEach((h) => seenAlertIds.current.add(`${h.ticker}|${h.company}`));
      // Skip toasts on the first run right after login — user just landed, don't blast them.
      if (fresh.length && !firstRun) {
        if (navigator.vibrate) navigator.vibrate([15, 40, 15]);
        import("sonner").then(({ toast }) => {
          fresh.forEach((f) => toast(`${f.ticker || f.company} prices ${f.trigger}`, {
            description: "Check the IPO calendar", action: { label: "Open", onClick: () => navigate("/ipos") },
          }));
        });
      }
      firstRun = false;
      setIpoHot(hot);
    }).catch(() => {});
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Drawing Alerts — poll every 60s: for each chart-draw hline in localStorage,
  // compare current asset price against the line. Fire a toast once when a
  // fresh cross happens (up or down through the level).
  useEffect(() => {
    if (!user) return;
    const lastPrices = new Map();  // symbol -> lastKnownPrice
    const load = async () => {
      let anyDraw = false;
      const items = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith("chartDraws:")) continue;
        const symbolKey = k.slice("chartDraws:".length);   // 'stock:AAPL' or 'crypto:bitcoin'
        if (!symbolKey.includes(":")) continue;
        let arr = [];
        try { arr = JSON.parse(localStorage.getItem(k) || "[]"); } catch {}
        const hlines = arr.filter((d) => d.kind === "hline");
        if (!hlines.length) continue;
        anyDraw = true;
        items.push({ symbolKey, hlines });
      }
      if (!anyDraw) return;

      await Promise.all(items.map(async ({ symbolKey, hlines }) => {
        const [asset_type, symbol] = symbolKey.split(":");
        try {
          const { data: asset } = await api.get(`/assets/${asset_type}/${symbol}`);
          const px = Number(asset.price);
          if (!(px > 0)) return;
          const prev = lastPrices.get(symbolKey);
          lastPrices.set(symbolKey, px);
          if (prev == null) return;   // need a baseline
          for (const line of hlines) {
            const level = Number(line.price);
            const crossedUp = prev < level && px >= level;
            const crossedDown = prev > level && px <= level;
            if (!crossedUp && !crossedDown) continue;
            const dir = crossedUp ? "up" : "down";
            const key = `${symbolKey}|${level.toFixed(2)}|${dir}|${Math.floor(Date.now() / 900000)}`;   // dedupe within 15min bucket
            if (firedDrawAlerts.current.has(key)) continue;
            firedDrawAlerts.current.add(key);
            if (navigator.vibrate) navigator.vibrate(20);
            const { toast } = await import("sonner");
            toast(`${asset.ticker || symbol.toUpperCase()} crossed ${dir} through $${level.toFixed(2)}`, {
              description: `Now at $${px.toFixed(2)}. Watch for follow-through.`,
              action: { label: "Open", onClick: () => navigate(`/asset/${asset_type}/${symbol}`) },
            });
            // Also journal it — attach a note to the user's most-recent order for this symbol
            try {
              const { data: orders } = await api.get("/orders");
              const target = (orders || []).find((o) => o.symbol === symbol && o.asset_type === asset_type);
              if (target) {
                const extra = `[alert] ${new Date().toLocaleDateString()} — price crossed ${dir} through $${level.toFixed(2)} (now $${px.toFixed(2)})`;
                const newNotes = target.notes ? `${target.notes} · ${extra}` : extra;
                await api.patch(`/orders/${target.id}/notes`, { notes: newNotes });
              }
            } catch { /* silent */ }
          }
        } catch { /* symbol unavailable, skip */ }
      }));
    };
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Live alerts polling — fills + price targets. Buzz + toast.
  useEffect(() => {
    if (!user) return;
    let since = new Date().toISOString();
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/events?since=${encodeURIComponent(since)}`);
        if (data.length) {
          since = data[0].t;
          data.slice().reverse().forEach((e) => {
            if (e.kind === "price_alert" || (e.kind === "fill" && e.body?.includes("ibkr"))) {
              if (navigator.vibrate) navigator.vibrate([15, 30, 15]);
              import("sonner").then(({ toast }) => toast(e.title, { description: e.body }));
            }
          });
        }
      } catch {}
      if (!stopped) setTimeout(tick, 15000);
    };
    tick();
    return () => { stopped = true; };
  }, [user]);

  useEffect(() => { setDrawer(false); }, [navigate]);

  // Live WebSocket → surface backend "alert" messages as Sonner toasts the
  // moment they arrive. Backend fans out price alerts, pattern hits, indicator
  // rule crosses, and stop-loss / take-profit triggers.
  const streamStatus = useLiveStream().status;
  const alertCb = useCallback((msg) => {
    // Native/PWA haptic feedback keyed to alert severity
    const kind = msg.kind || "";
    if (kind.includes("loss") || kind === "risk_halt") native.haptic("error");
    else if (kind === "take_profit" || kind === "earnings_withdrawn" || kind === "signal_earning") native.haptic("success");
    else native.haptic("light");
    import("sonner").then(({ toast }) => {
      const action = msg.symbol
        ? { label: "Open", onClick: () => navigate(`/asset/${msg.asset_type || "stock"}/${msg.symbol}`) }
        : undefined;
      toast(msg.title || "Aanar alert", { description: msg.body, action, duration: 8000 });
    });
  }, [navigate]);
  useAlerts(alertCb);

  const SidebarInner = ({ onNavigate }) => (
    <>
      <div className="px-6 py-5 border-b border-border flex items-center justify-between">
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5" data-testid="brand-link">
          <img src="/brand/aanar-mark-reversed.svg" alt="Aanar" className="w-9 h-9 dark:block hidden" />
          <img src="/brand/aanar-mark.svg" alt="Aanar" className="w-9 h-9 dark:hidden block" />
          <div>
            <div className="font-display font-bold text-lg leading-none tracking-tight">Aanar</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">own the market</div>
          </div>
        </Link>
        <button onClick={onNavigate} className="md:hidden text-muted-foreground p-1" data-testid="drawer-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {navGroups.map((item) => {
          if (item.children) {
            return (
              <NavGroup
                key={item.label}
                group={item}
                currentPath={location.pathname}
                onNavigate={onNavigate}
              />
            );
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border space-y-2">
        <div className="px-3 py-2 rounded-lg bg-secondary/50">
          <div className="text-xs text-muted-foreground">Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="user-name">{user?.full_name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setDark((d) => !d)} data-testid="theme-toggle" className="flex-1 rounded-full">
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn" className="flex-1 rounded-full">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </>
  );

  const stopAll = async () => {
    try {
      const { data } = await api.post("/bots/stop-all");
      if (data.stopped) navigate(0);
    } catch {}
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border bg-card flex-col" data-testid="sidebar">
        <SidebarInner onNavigate={() => {}} />
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={() => setDrawer(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-card border-r border-border flex flex-col shadow-xl">
            <SidebarInner onNavigate={() => setDrawer(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 md:h-16 border-b border-border bg-card/70 backdrop-blur-md flex items-center justify-between px-4 md:px-8 sticky top-0 z-30" data-testid="header">
          <div className="flex items-center gap-3 md:gap-6 min-w-0">
            <button onClick={() => setDrawer(true)} className="md:hidden p-1.5 -ml-1.5 text-muted-foreground" data-testid="menu-btn">
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/" className="md:hidden flex items-center gap-1.5">
              <img src="/brand/aanar-mark-reversed.svg" alt="Aanar" className="w-7 h-7 dark:block hidden" />
              <img src="/brand/aanar-mark.svg" alt="Aanar" className="w-7 h-7 dark:hidden block" />
              <span className="font-display font-bold text-base tracking-tight">Aanar</span>
            </Link>
            <div className="hidden md:block">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Virtual balance</div>
              <div className="font-mono-tab text-lg font-medium" data-testid="cash-balance">
                {fmtMoney(user?.cash_balance)}
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-2 pl-6 border-l border-border">
              <Badge variant="outline" className="rounded-full uppercase text-[10px] tracking-wider" data-testid="mode-badge">
                {user?.account_mode || "paper"}
              </Badge>
              {ibkr?.connected ? (
                <Badge variant="secondary" className="rounded-full text-[10px] flex gap-1 items-center" data-testid="ibkr-badge">
                  <Link2 className="w-3 h-3" /> IBKR {ibkr.mode}
                </Badge>
              ) : (
                <button onClick={() => navigate("/settings")} data-testid="ibkr-connect-cta"
                        className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground border border-dashed border-border rounded-full px-2 py-0.5">
                  Connect IBKR
                </button>
              )}
              {streamStatus === "open" && (
                <span data-testid="ws-live" className="hidden md:inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[hsl(var(--gain))]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gain))] animate-pulse" />
                  live
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* IPO alert bell */}
            <button
              onClick={() => navigate("/ipos")}
              data-testid="ipo-bell"
              className="relative p-2 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              title={ipoHot.length ? `${ipoHot.length} IPO(s) pricing soon` : "IPO alerts"}>
              {ipoHot.length > 0 ? <BellRing className="w-4 h-4 text-[hsl(var(--ai))]" /> : <Bell className="w-4 h-4" />}
              {ipoHot.length > 0 && (
                <span data-testid="ipo-bell-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--ai))] text-background text-[9px] font-bold flex items-center justify-center">
                  {ipoHot.length}
                </span>
              )}
            </button>
            {/* Compact balance for mobile */}
            <div className="md:hidden text-right">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Cash</div>
              <div className="font-mono-tab text-sm font-medium" data-testid="cash-balance-mobile">{fmtMoney(user?.cash_balance, 0)}</div>
            </div>
            <NotificationBell />
            <KillSwitchButton />
            <Button onClick={stopAll} variant="outline" size="sm" data-testid="kill-switch"
                    className="hidden sm:inline-flex rounded-full border-[hsl(var(--loss))]/40 text-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/10">
              Stop all bots
            </Button>
            <Button onClick={() => navigate("/markets")} data-testid="header-trade-btn"
                    className="hidden sm:inline-flex rounded-full bg-foreground text-background hover:bg-foreground/90">
              Explore markets
            </Button>
          </div>
        </header>

        {/* Main content — extra bottom padding on mobile for bottom nav */}
        <main className="flex-1 overflow-y-auto px-4 md:px-8 py-6 md:py-8 pb-24 md:pb-8">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-border" data-testid="bottom-nav">
          <div className="grid grid-cols-5">
            {bottomNav.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end}
                       data-testid={`bottom-nav-${label.toLowerCase()}`}
                       className={({ isActive }) =>
                         `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium ${
                           isActive ? "text-foreground" : "text-muted-foreground"
                         }`
                       }>
                <Icon className="w-5 h-5" />
                {label}
              </NavLink>
            ))}
          </div>
          <div className="h-[env(safe-area-inset-bottom)]" />
        </nav>
      </div>
    </div>
  );
}
